import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Printer,
  FilePdf,
  PencilSimple,
  Trash,
  Camera,
  Briefcase,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { ConvertHoldDialog } from "@/components/ConvertHoldDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AppHeader } from "@/components/AppHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { PhotoGallery } from "@/components/PhotoGallery";
import { FileVault } from "@/components/FileVault";
import { RemnantAllocation } from "@/components/RemnantAllocation";
import { SinkAssign } from "@/components/SinkAssign";
import { LabelSheet } from "@/components/LabelSheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { shrinkForUpload } from "@/lib/shrinkImage";
import { useDirectory } from "@/lib/directory";

export default function ReservationDetail() {
  const { id } = useParams();
  const { nameOf } = useDirectory();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [allSlabs, setAllSlabs] = useState([]);
  const [pickSlab, setPickSlab] = useState("none");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const labelRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(
    () =>
      api
        .get(`/reservations/${id}`)
        .then(({ data }) => setR(data))
        .catch(() => toast.error("Reservation not found")),
    [id],
  );

  const loadSlabs = useCallback(
    () => api.get("/slabs").then(({ data }) => setAllSlabs(data)),
    [],
  );

  useEffect(() => {
    load();
    loadSlabs();
  }, [load, loadSlabs]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", await shrinkForUpload(file));
    try {
      const { data } = await api.post(`/reservations/${id}/photos`, fd);
      setR(data);
      toast.success("Photo attached");
    } catch (err) {
      toast.error("Photo upload failed");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = async (photoId) => {
    const { data } = await api.delete(`/reservations/${id}/photos/${photoId}`);
    setR(data);
    toast.success("Photo removed");
  };

  const downloadPdf = async () => {
    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import("jspdf"),
      import("html2canvas"),
    ]);
    const canvas = await html2canvas(labelRef.current, {
      scale: 3,
      backgroundColor: "#ffffff",
    });
    const pdf = new jsPDF({
      unit: "in",
      format: [4, 6],
      orientation: "portrait",
    });
    pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, 4, 6);
    pdf.save(`${r.tag}-label.pdf`);
    toast.success("Label PDF downloaded");
  };

  const startEdit = (panel, fields) => {
    setEditing(panel);
    setDraft(Object.fromEntries(fields.map((f) => [f, r[f] ?? ""])));
  };

  const savePanel = async () => {
    setSaving(true);
    try {
      const payload = { ...draft };
      if ("quantity" in payload)
        payload.quantity = Number(payload.quantity) || 1;
      for (const k of ["length_in", "width_in"]) {
        if (k in payload)
          payload[k] = payload[k] === "" ? null : Number(payload[k]);
      }
      const { data } = await api.patch(`/reservations/${id}`, payload);
      setR(data);
      setEditing(null);
      toast.success("Saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
    setSaving(false);
  };

  const remove = async () => {
    await api.delete(`/reservations/${id}`);
    toast.success("Reservation deleted");
    navigate("/inventory/material-on-hold");
  };

  if (!r)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Reservation" />
        <div className="mx-auto max-w-3xl px-4 pt-10">
          <div className="h-40 animate-pulse rounded-lg border border-zinc-800 bg-zinc-900" />
        </div>
      </div>
    );

  const photos = (r.photos || []).filter((p) => !p.is_deleted);

  const rows = [
    ["Customer", r.customer_name],
    ["Project", r.project_name],
    ["Slab Color", r.slab_color],
    ["Install Date", r.install_date || "—"],
    [
      "Dimensions",
      r.length_in || r.width_in
        ? `${r.length_in || "?"}" x ${r.width_in || "?"}"`
        : "—",
    ],
    ["Quantity", String(r.quantity)],
    ...(r.source ? [["Source", r.source]] : []),
    ...(r.source === "Website hold" && r.hold_ends_at
      ? [["Auto-releases", new Date(r.hold_ends_at).toLocaleString()]]
      : []),
    ["Created By", nameOf(r.created_by) || "—"],
    ["Last Updated By", nameOf(r.updated_by) || nameOf(r.created_by) || "—"],
  ];

  const heldSlabs = allSlabs.filter((s) => (r.slab_ids || []).includes(s.id));
  const freeSlabs = allSlabs.filter((s) => s.status === "In Stock");

  const holdSlab = async (slabId, release) => {
    try {
      await api.post(`/reservations/${id}/slabs`, { slab_id: slabId, release });
      toast.success(
        release ? "Slab released back to stock" : "Slab reserved for this hold",
      );
      setPickSlab("none");
      loadSlabs();
      load();
    } catch {
      toast.error("Could not update slab");
    }
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title={r.tag} />
      <main className="relative z-10 mx-auto max-w-5xl px-4 pb-32 pt-6">
        <button
          onClick={() => navigate("/inventory/material-on-hold")}
          data-testid="back-button"
          className="no-print mb-4 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors duration-150 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" /> All material on hold
        </button>

        <div
          className="no-print rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="reservation-header-card"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="font-mono-data text-xs uppercase tracking-widest text-zinc-500">
                {r.tag}
              </div>
              <h1
                className="text-2xl font-bold tracking-tight sm:text-3xl"
                data-testid="detail-customer"
              >
                {r.customer_name}
              </h1>
              <p className="mt-1 text-zinc-400">{r.project_name}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {r.job_id ? (
                <button
                  type="button"
                  data-testid="hold-job-link"
                  onClick={() => navigate(`/projects/${r.job_id}?tab=field`)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-indigo-400/40 bg-indigo-500/10 px-3 text-[10px] font-bold uppercase tracking-widest text-indigo-300 transition-colors duration-150 hover:bg-indigo-500/20"
                >
                  <Briefcase size={14} weight="bold" /> Job {r.job_code}
                </button>
              ) : (
                ["Requested", "Reserved"].includes(r.status) && (
                  <Button
                    data-testid="convert-hold-button"
                    onClick={() => setConverting(true)}
                    title="Open a job for this customer with the held material reserved to it"
                    className="h-9 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                  >
                    <Briefcase size={14} weight="bold" className="mr-1.5" /> Convert to job
                  </Button>
                )
              )}
              <StatusBadge status={r.status} testId="detail-status-badge" />
            </div>
          </div>
        </div>
        {converting && (
          <ConvertHoldDialog
            hold={r}
            open={converting}
            onClose={(data) => {
              setConverting(false);
              if (data?.reservation) setR(data.reservation);
            }}
          />
        )}

        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_auto]">
          <div>
            {" "}
            <div
              className="rounded-lg border border-zinc-800 bg-zinc-900"
              data-testid="details-panel"
            >
              <div className="flex items-center justify-between gap-3 border-b border-zinc-800 px-4 py-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Hold Details
                </div>
                {editing === "details" ? (
                  <div className="flex gap-2">
                    <Button
                      data-testid="save-details-button"
                      disabled={saving}
                      onClick={savePanel}
                      className="h-9 bg-indigo-500 text-[10px] uppercase tracking-widest text-white hover:bg-indigo-400"
                    >
                      Save
                    </Button>
                    <Button
                      data-testid="cancel-details-button"
                      variant="outline"
                      onClick={() => setEditing(null)}
                      className="h-9 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <button
                    data-testid="edit-details-panel"
                    onClick={() =>
                      startEdit("details", [
                        "customer_name",
                        "project_name",
                        "slab_color",
                        "install_date",
                        "length_in",
                        "width_in",
                        "quantity",
                        "status",
                      ])
                    }
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-zinc-500 hover:text-yellow-400"
                  >
                    <PencilSimple size={13} weight="bold" /> Edit
                  </button>
                )}
              </div>
              {editing === "details" ? (
                <div className="grid gap-3 p-4 sm:grid-cols-2">
                  {[
                    ["customer_name", "Customer"],
                    ["project_name", "Project"],
                    ["slab_color", "Slab color"],
                    ["install_date", "Install date"],
                    ["length_in", "Length (in)"],
                    ["width_in", "Width (in)"],
                    ["quantity", "Quantity"],
                  ].map(([k, label]) => (
                    <div key={k} className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {label}
                      </Label>
                      <Input
                        data-testid={`edit-${k}`}
                        value={draft[k] ?? ""}
                        onChange={(e) =>
                          setDraft({ ...draft, [k]: e.target.value })
                        }
                        className="h-12 border-zinc-800 bg-zinc-950"
                      />
                    </div>
                  ))}
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Status
                    </Label>
                    <Select
                      value={draft.status}
                      onValueChange={(v) => setDraft({ ...draft, status: v })}
                    >
                      <SelectTrigger
                        data-testid="edit-status"
                        className="h-12 border-zinc-800 bg-zinc-950"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-zinc-800 bg-zinc-900">
                        {["Reserved", "Fabricating", "Installed"].map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {rows.map(([k, v]) => (
                    <div
                      key={k}
                      className="flex items-center justify-between gap-4 px-4 py-3"
                    >
                      <span className="text-xs uppercase tracking-widest text-zinc-500">
                        {k}
                      </span>
                      <span
                        data-testid={`detail-${k.toLowerCase().replace(/ /g, "-")}`}
                        className="font-mono-data text-right text-sm font-medium"
                      >
                        {v}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div
              className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              data-testid="sink-panel"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Sink
                </div>
                {editing === "sink" ? (
                  <div className="flex gap-2">
                    <Button
                      data-testid="save-sink-button"
                      disabled={saving}
                      onClick={savePanel}
                      className="h-9 bg-indigo-500 text-[10px] uppercase tracking-widest text-white hover:bg-indigo-400"
                    >
                      Save
                    </Button>
                    <Button
                      data-testid="cancel-sink-button"
                      variant="outline"
                      onClick={() => setEditing(null)}
                      className="h-9 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <button
                    data-testid="edit-sink-panel"
                    onClick={() =>
                      startEdit("sink", ["sink", "sink_location", "sink_notes"])
                    }
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-zinc-500 hover:text-yellow-400"
                  >
                    <PencilSimple size={13} weight="bold" /> Edit
                  </button>
                )}
              </div>
              {editing === "sink" ? (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Sink type / model
                    </Label>
                    <Input
                      data-testid="edit-sink"
                      value={draft.sink ?? ""}
                      onChange={(e) =>
                        setDraft({ ...draft, sink: e.target.value })
                      }
                      className="h-12 border-zinc-800 bg-zinc-950"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Location
                    </Label>
                    <Select
                      value={draft.sink_location || "Warehouse"}
                      onValueChange={(v) =>
                        setDraft({ ...draft, sink_location: v })
                      }
                    >
                      <SelectTrigger
                        data-testid="edit-sink_location"
                        className="h-12 border-zinc-800 bg-zinc-950"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-zinc-800 bg-zinc-900">
                        {["Warehouse", "On-Site", "Not supplied"].map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Shop notes
                    </Label>
                    <Textarea
                      data-testid="edit-sink_notes"
                      rows={3}
                      value={draft.sink_notes ?? ""}
                      onChange={(e) =>
                        setDraft({ ...draft, sink_notes: e.target.value })
                      }
                      className="border-zinc-800 bg-zinc-950"
                    />
                  </div>
                </div>
              ) : (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {[
                    ["Type", r.sink || "—", "detail-sink"],
                    [
                      "Location",
                      r.sink_location || "—",
                      "detail-sink-location",
                    ],
                  ].map(([k, v, tid]) => (
                    <div
                      key={k}
                      className="rounded-md border border-zinc-800 bg-zinc-950 p-3"
                    >
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {k}
                      </div>
                      <div
                        data-testid={tid}
                        className="mt-1 text-sm font-semibold"
                      >
                        {v}
                      </div>
                    </div>
                  ))}
                  <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 sm:col-span-2">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Shop notes
                    </div>
                    <p
                      data-testid="detail-sink-notes"
                      className="mt-1 whitespace-pre-wrap text-sm text-zinc-200"
                    >
                      {r.sink_notes || "—"}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="no-print mt-6">
              <SinkAssign entity="reservation" entityId={id} />
            </div>
            <div
              className="no-print mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              data-testid="hold-slabs-panel"
            >
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Slabs reserved from inventory
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Select value={pickSlab} onValueChange={setPickSlab}>
                  <SelectTrigger
                    data-testid="hold-slab-select"
                    className="h-12 flex-1 border-zinc-800 bg-zinc-950 sm:w-72 sm:flex-none"
                  >
                    <SelectValue placeholder="Pick a slab in stock" />
                  </SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-900">
                    <SelectItem value="none">Pick a slab in stock</SelectItem>
                    {freeSlabs.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.code} — {s.slab_color} {s.length_in}"x{s.width_in}"
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  data-testid="reserve-slab-button"
                  disabled={pickSlab === "none"}
                  onClick={() => holdSlab(pickSlab, false)}
                  className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                >
                  Reserve Slab
                </Button>
              </div>
              <div className="mt-3 space-y-2">
                {heldSlabs.length === 0 && (
                  <p className="text-sm text-zinc-500">
                    No slabs reserved for this hold yet.
                  </p>
                )}
                {heldSlabs.map((s) => (
                  <div
                    key={s.id}
                    data-testid={`held-slab-${s.id}`}
                    className="flex items-center gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="font-mono-data block text-xs text-yellow-500">
                        {s.code}
                      </span>
                      <span className="block truncate text-sm font-semibold">
                        {s.slab_color} · {s.length_in}" x {s.width_in}"
                      </span>
                    </span>
                    <button
                      data-testid={`release-slab-${s.id}`}
                      onClick={() => holdSlab(s.id, true)}
                      className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-red-400"
                    >
                      Release
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div
              className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              data-testid="comments-panel"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Fabrication Comments
                </div>
                {editing === "comments" ? (
                  <div className="flex gap-2">
                    <Button
                      data-testid="save-comments-button"
                      disabled={saving}
                      onClick={savePanel}
                      className="h-9 bg-indigo-500 text-[10px] uppercase tracking-widest text-white hover:bg-indigo-400"
                    >
                      Save
                    </Button>
                    <Button
                      data-testid="cancel-comments-button"
                      variant="outline"
                      onClick={() => setEditing(null)}
                      className="h-9 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <button
                    data-testid="edit-comments-panel"
                    onClick={() => startEdit("comments", ["comments"])}
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-zinc-500 hover:text-yellow-400"
                  >
                    <PencilSimple size={13} weight="bold" /> Edit
                  </button>
                )}
              </div>
              {editing === "comments" ? (
                <Textarea
                  data-testid="edit-comments"
                  rows={5}
                  value={draft.comments ?? ""}
                  onChange={(e) =>
                    setDraft({ ...draft, comments: e.target.value })
                  }
                  className="mt-3 border-zinc-800 bg-zinc-950"
                />
              ) : (
                <p
                  data-testid="detail-comments"
                  className="mt-2 whitespace-pre-wrap text-sm text-zinc-200"
                >
                  {r.comments || "—"}
                </p>
              )}
            </div>
            <div className="no-print mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Slab Photos
                </div>
                <Button
                  data-testid="add-photo-button"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="h-10 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-wider hover:bg-zinc-800"
                >
                  <Camera size={16} weight="bold" className="mr-2" />
                  {uploading ? "Uploading…" : "Add Photo"}
                </Button>
                <input
                  ref={fileRef}
                  data-testid="photo-file-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={uploadPhoto}
                  className="hidden"
                />
              </div>
              <PhotoGallery
                photos={photos}
                onRemove={removePhoto}
                testPrefix="photo"
              />
            </div>
            <div className="mt-6">
              <FileVault entity="reservations" entityId={id} emailJobId={id} />
            </div>
            <div className="mt-6">
              <RemnantAllocation jobId={id} />
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                data-testid="print-label-button"
                onClick={() => window.print()}
                className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
              >
                <Printer size={18} weight="bold" className="mr-2" /> Print 4x6
                Label
              </Button>
              <Button
                data-testid="download-pdf-button"
                variant="outline"
                onClick={downloadPdf}
                className="h-12 border-zinc-700 bg-transparent font-semibold uppercase tracking-wider hover:bg-zinc-800"
              >
                <FilePdf size={18} weight="bold" className="mr-2" /> PDF
              </Button>
              <Button
                data-testid="edit-reservation-button"
                variant="outline"
                onClick={() => navigate(`/reservation/${id}/edit`)}
                className="h-12 border-zinc-700 bg-transparent font-semibold uppercase tracking-wider hover:bg-zinc-800"
              >
                <PencilSimple size={18} weight="bold" className="mr-2" /> Edit
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    data-testid="delete-reservation-button"
                    variant="outline"
                    className="h-12 border-red-900/60 bg-transparent font-semibold uppercase tracking-wider text-red-400 hover:bg-red-950/40"
                  >
                    <Trash size={18} weight="bold" className="mr-2" /> Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="border-zinc-800 bg-zinc-900">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {r.tag}?</AlertDialogTitle>
                    <AlertDialogDescription className="text-zinc-400">
                      This permanently removes the reservation for{" "}
                      {r.customer_name}.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel
                      data-testid="cancel-delete-button"
                      className="border-zinc-700"
                    >
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="confirm-delete-button"
                      onClick={remove}
                      className="bg-red-600 text-white hover:bg-red-500"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4">
            <div className="mb-3 text-xs uppercase tracking-widest text-zinc-500">
              Label Preview — 4 x 6 in
            </div>
            <div
              id="print-area"
              className="origin-top-left scale-[0.72] sm:scale-100"
              style={{ width: "4in" }}
            >
              <LabelSheet r={r} slab={heldSlabs[0]} forwardRef={labelRef} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
